import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Eye, EyeOff, Search, Filter, Image, X, Upload, Settings } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FilterSelect from '../components/FilterSelect';
import FilterInput from '../components/FilterInput';
import api from '../services/api';
import { resolveFileUrl } from '@/lib/utils';

// Brand colors
const BRAND_BLUE = '#2B4B6A';
const BRAND_ORANGE = '#F58A5A';

const AdminAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: '', content: '', is_published: false, category_id: ''
  });
  
  // File upload state
  const [showFileModal, setShowFileModal] = useState(false);
  const [fileAnnouncement, setFileAnnouncement] = useState(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photoInputRef = useRef(null);
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterOrder, setFilterOrder] = useState('newest');
  const [filterStatus, setFilterStatus] = useState('published');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [announcementsRes, categoriesRes] = await Promise.all([
        api.get('/admin/announcements'),
        api.get('/announcement-categories')
      ]);
      setAnnouncements(announcementsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        category_id: formData.category_id || null
      };
      
      if (editingAnnouncement) {
        await api.put(`/admin/announcements/${editingAnnouncement.id}`, dataToSend);
        toast.success('Announcement updated');
      } else {
        await api.post('/admin/announcements', dataToSend);
        toast.success('Announcement created');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save');
    }
  };

  const handleEdit = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      is_published: announcement.is_published === true || announcement.is_published === 1,
      category_id: announcement.category_id || ''
    });
    setShowModal(true);
  };

  const handleTogglePublish = async (announcement) => {
    try {
      await api.put(`/admin/announcements/${announcement.id}`, {
        is_published: !announcement.is_published
      });
      toast.success(announcement.is_published ? 'Unpublished' : 'Published');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const resetForm = () => {
    setEditingAnnouncement(null);
    setFormData({ title: '', content: '', is_published: false, category_id: '' });
  };

  // File upload handlers
  const openFileModal = (announcement) => {
    setFileAnnouncement(announcement);
    setShowFileModal(true);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const currentPhotos = fileAnnouncement.photos?.length || 0;
    if (currentPhotos + files.length > 5) {
      toast.error(`Maximum 5 photos allowed. You have ${currentPhotos}, trying to add ${files.length}`);
      return;
    }

    setUploadingPhotos(true);
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));

    try {
      const res = await api.post(`/uploads/announcements/${fileAnnouncement.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded ${files.length} photo(s)`);
      setFileAnnouncement({ ...fileAnnouncement, photos: res.data.photos });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setUploadingPhotos(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleDeletePhoto = async (filename) => {
    try {
      const res = await api.delete(`/uploads/announcements/${fileAnnouncement.id}/photos/${filename}`);
      toast.success('Photo deleted');
      setFileAnnouncement({ ...fileAnnouncement, photos: res.data.photos });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  // Apply filters
  const filteredAnnouncements = announcements
    .filter(ann => {
      if (filterCategory && ann.category_id !== filterCategory) return false;
      if (filterSearch) {
        const search = filterSearch.toLowerCase();
        if (!ann.title.toLowerCase().includes(search) && !(ann.content || '').toLowerCase().includes(search)) return false;
      }
      // Status filter
      if (filterStatus === 'published' && !ann.is_published) return false;
      if (filterStatus === 'draft' && ann.is_published) return false;
      return true;
    })
    .sort((a, b) => {
      if (filterOrder === 'newest') return new Date(b.created_at) - new Date(a.created_at);
      if (filterOrder === 'oldest') return new Date(a.created_at) - new Date(b.created_at);
      return 0;
    });

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: BRAND_BLUE }}></div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Announcements</h1>
            <p className="text-gray-500 text-sm mt-1">Manage platform announcements</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/announcement-categories"
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition text-sm font-medium"
            >
              <Settings className="w-4 h-4" />
              Manage Categories
            </Link>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="text-white px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 text-sm"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <Plus className="w-5 h-5" />
              New Announcement
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6 flex flex-wrap gap-4 items-center shadow-sm">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-600">Filters:</span>
          </div>
          
          <FilterSelect
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            options={[
              { value: 'published', label: 'Published Only' },
              { value: 'draft', label: 'Drafts Only' },
              { value: 'all', label: 'All Status' }
            ]}
            data-testid="filter-status"
          />
          
          <FilterSelect
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            options={[
              { value: '', label: 'All Categories' },
              ...categories.map(cat => ({ value: cat.id, label: cat.name }))
            ]}
          />
          
          <FilterSelect
            value={filterOrder}
            onChange={(e) => setFilterOrder(e.target.value)}
            options={[
              { value: 'newest', label: 'Newest First' },
              { value: 'oldest', label: 'Oldest First' }
            ]}
          />
          
          <FilterInput
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            placeholder="Search announcements..."
            className="flex-1 min-w-[200px]"
          />
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {editingAnnouncement ? 'Edit Announcement' : 'New Announcement'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={formData.category_id}
                    onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                  >
                    <option value="">No Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Content *</label>
                  <textarea
                    required
                    value={formData.content}
                    onChange={(e) => setFormData({...formData, content: e.target.value})}
                    rows={8}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:outline-none text-sm"
                    placeholder="HTML supported"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_published"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({...formData, is_published: e.target.checked})}
                    className="rounded border-gray-300"
                    style={{ accentColor: BRAND_BLUE }}
                  />
                  <label htmlFor="is_published" className="text-sm text-gray-700">Publish immediately</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 text-sm">Cancel</button>
                  <button type="submit" className="flex-1 text-white px-4 py-2.5 rounded-xl font-medium text-sm" style={{ backgroundColor: BRAND_BLUE }}>{editingAnnouncement ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* File Upload Modal */}
        {showFileModal && fileAnnouncement && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Manage Photos: {fileAnnouncement.title}</h2>
                <button onClick={() => setShowFileModal(false)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Photos Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-700 flex items-center gap-2">
                    <Image className="w-4 h-4" /> Photos ({fileAnnouncement.photos?.length || 0}/5)
                  </h3>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhotos || (fileAnnouncement.photos?.length || 0) >= 5}
                    className="flex items-center gap-2 px-3 py-1.5 text-white text-sm rounded-xl disabled:opacity-50"
                    style={{ backgroundColor: BRAND_BLUE }}
                  >
                    <Upload className="w-4 h-4" />
                    {uploadingPhotos ? 'Uploading...' : 'Add Photos'}
                  </button>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {(fileAnnouncement.photos || []).map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={resolveFileUrl(photo.url, process.env.REACT_APP_BACKEND_URL)}
                        alt={photo.filename}
                        className="w-full h-24 object-cover rounded-xl border border-gray-200"
                      />
                      <button
                        onClick={() => handleDeletePhoto(photo.stored_name)}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {(fileAnnouncement.photos?.length || 0) === 0 && (
                    <p className="text-sm text-gray-400 col-span-4">No photos uploaded</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Announcements Table */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-100">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Title</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Category</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Photos</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Created</th>
                <th className="text-left px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-center px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredAnnouncements.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-12 text-center text-gray-500">No announcements found.</td></tr>
              ) : (
                filteredAnnouncements.map((announcement) => (
                  <tr key={announcement.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">{announcement.title}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {announcement.category_name ? (
                        <span className="bg-gray-100 px-2.5 py-1 rounded-lg text-xs">{announcement.category_name}</span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Image className="w-3 h-3" /> {announcement.photos?.length || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{new Date(announcement.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      {announcement.is_published ? (
                        <span className="bg-green-50 text-green-600 px-2.5 py-1 rounded-full text-xs">Published</span>
                      ) : (
                        <span className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full text-xs">Draft</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openFileModal(announcement)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Manage Photos">
                          <Upload className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleTogglePublish(announcement)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title={announcement.is_published ? 'Unpublish' : 'Publish'}>
                          {announcement.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button onClick={() => handleEdit(announcement)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Edit">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(announcement.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminAnnouncements;
